import { el, setText } from "../lib/dom.js";
import { createIcon } from "../render/icon.js";
import type { PowerboxRequest } from "../host/broker.js";
import { createObject, type ObjectSnapshot, snapshot, type WorkspaceDoc } from "../workspace/schema.js";
import css from "./Powerbox.module.css";

const DEFAULT_TYPE = "text/note";

/**
 * The powerbox (§5.4).
 *
 * Read the shape of this before adding to it. There is no "allow this app to
 * access X?" step, and there never will be one: the picker *is* the grant. The
 * user choosing a note is the same action as authorising the app to have it,
 * which is why an app can hold a capability it was never separately approved
 * for, and why the user cannot approve something they did not pick.
 *
 * Prototype scope is what §5.4 asks for and no more: a modal listing objects
 * that match the filter, plus "New…".
 */
export class Powerbox {
  readonly el: HTMLElement;

  readonly #ws: WorkspaceDoc;
  readonly #title: HTMLElement;
  readonly #subtitle: HTMLElement;
  readonly #list: HTMLElement;
  readonly #newName: HTMLInputElement;
  readonly #create: HTMLElement;
  #settle: ((objectId: string | null) => void) | null = null;
  #request: PowerboxRequest | null = null;
  /**
   * The environments this workspace can offer for an `exec` request (§10.3).
   * Empty until a Sprite exists, and the picker says so rather than offering a
   * choice that cannot be honoured.
   */
  #environments: { name: string; detail: string }[] = [];

  constructor(ws: WorkspaceDoc) {
    this.#ws = ws;

    this.#title = el("div", { class: css.title ?? "" });
    this.#subtitle = el("div", { class: css.subtitle ?? "" });
    this.#list = el("div", { class: css.list ?? "", "data-role": "powerbox-list" });
    this.#newName = el("input", {
      class: css.newName ?? "",
      "data-role": "powerbox-name",
      placeholder: "Name a new one…",
    });

    const create = el(
      "button",
      {
        type: "button",
        class: css.action ?? "",
        "data-variant": "primary",
        "data-role": "powerbox-create",
      },
      [createIcon("plus", 13), "New…"],
    );
    create.addEventListener("click", () => this.#createAndGrant());
    this.#create = create;
    this.#newName.addEventListener("keydown", (event) => {
      if (event.key === "Enter") this.#createAndGrant();
      if (event.key === "Escape") this.#finish(null);
    });

    const cancel = el(
      "button",
      { type: "button", class: css.action ?? "", "data-role": "powerbox-cancel" },
      ["Cancel"],
    );
    cancel.addEventListener("click", () => this.#finish(null));

    const panel = el("div", { class: css.panel ?? "", "data-role": "powerbox" }, [
      el("div", { class: css.head ?? "" }, [this.#title, this.#subtitle]),
      this.#list,
      el("div", { class: css.foot ?? "" }, [this.#newName, create, cancel]),
    ]);

    this.el = el("div", { class: css.overlay ?? "", "data-role": "powerbox-overlay", hidden: true }, [
      panel,
    ]);
    this.el.addEventListener("click", (event) => {
      if (event.target === this.el) this.#finish(null);
    });
  }

  /** What `exec` requests may choose from. Set by the shell as Sprites appear. */
  setEnvironments(environments: { name: string; detail: string }[]): void {
    this.#environments = environments;
  }

  /**
   * Show the picker. Resolves with what the user chose, named in the namespace
   * of the kind asked for: an object id, or — for `exec` — a Sprite name.
   */
  pick(request: PowerboxRequest): Promise<string | null> {
    // One picker at a time: a second request while one is open is refused
    // rather than queued, so an app cannot stack modals in front of the user.
    if (this.#settle !== null) return Promise.resolve(null);

    const isExec = request.kind === "exec";
    this.#request = request;
    setText(
      this.#title,
      request.filter?.purpose ?? (isExec ? "Choose an environment" : "Choose an object"),
    );
    this.#subtitle.replaceChildren(
      el("span", { class: css.appName ?? "" }, [request.appId]),
      document.createTextNode(
        isExec
          ? " is asking to run something"
          : request.kind === "collection"
            ? " is asking for a set of objects"
            : " is asking for one object",
      ),
    );
    this.#newName.value = "";
    // There is nothing to name into existence when choosing an environment: a
    // Sprite is provisioned from the workspace's flake, not typed in here.
    this.#newName.hidden = isExec;
    this.#create.hidden = isExec;
    if (isExec) this.#renderEnvironments();
    else this.#renderList(request);
    this.el.hidden = false;
    this.#newName.focus();

    return new Promise<string | null>((resolve) => {
      this.#settle = resolve;
    });
  }

  #renderEnvironments(): void {
    this.#list.replaceChildren(
      ...(this.#environments.length === 0
        ? [
            el("div", { class: css.empty ?? "" }, [
              "No environment is running yet. One is being built from this workspace’s flake.",
            ]),
          ]
        : this.#environments.map((environment) => {
            const row = el(
              "button",
              {
                type: "button",
                class: css.row ?? "",
                "data-role": "powerbox-row",
                "data-object": environment.name,
              },
              [
                createIcon("terminal", 13, "accent"),
                el("span", { class: css.rowName ?? "" }, [environment.name]),
                el("span", { class: css.rowType ?? "" }, [environment.detail]),
              ],
            );
            row.addEventListener("click", () => this.#finish(environment.name));
            return row;
          })),
    );
  }

  #renderList(request: PowerboxRequest): void {
    const types = request.filter?.objectTypes;
    // The *user* is choosing, and the user's authority is not scoped — so the
    // picker lists the whole workspace, filtered only by what the app can use.
    const matching = snapshot(this.#ws).objects.filter(
      (object) => types === undefined || types.includes(object.type),
    );

    this.#list.replaceChildren(
      ...(matching.length === 0
        ? [
            el("div", { class: css.empty ?? "" }, [
              types === undefined
                ? "Nothing in this workspace yet. Make one below."
                : `No ${types.join(" or ")} objects yet. Make one below.`,
            ]),
          ]
        : matching.map((object) => this.#row(object))),
    );
  }

  #row(object: ObjectSnapshot): HTMLElement {
    const row = el(
      "button",
      {
        type: "button",
        class: css.row ?? "",
        "data-role": "powerbox-item",
        "data-object": object.id,
        "data-object-name": object.name,
      },
      [
        createIcon("note", 14, "muted"),
        el("span", { class: css.rowName ?? "" }, [object.name]),
        el("span", { class: css.rowType ?? "" }, [object.type]),
      ],
    );
    row.addEventListener("click", () => this.#finish(object.id));
    return row;
  }

  #createAndGrant(): void {
    const request = this.#request;
    if (request === null) return;
    const name = this.#newName.value.trim();
    const type = request.filter?.objectTypes?.[0] ?? DEFAULT_TYPE;
    // Created as the *user*, not as the app: it is the user's object, and the
    // app receives it because they chose to hand it over.
    const id = createObject(this.#ws, {
      type,
      name: name === "" ? "Untitled" : name,
      owner: request.appId,
      now: Date.now(),
    });
    this.#finish(id);
  }

  #finish(objectId: string | null): void {
    const settle = this.#settle;
    this.#settle = null;
    this.#request = null;
    this.el.hidden = true;
    settle?.(objectId);
  }
}
